import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import {
  ArrowLeft,
  FileText,
  Calendar,
  CheckCircle,
  XCircle,
  Eye,
  X,
  Download,
} from 'lucide-react';
import { submissionsAPI, adminAPI, resolveServerUrl } from '../../services/api';
import mammoth from 'mammoth';

interface SubmissionDetails {
  id: number;
  title: string;
  category_name: string;
  description: string;
  status: string;
  submitted_date: string;
  reviewed_date?: string;
  feedback?: string;
  faculty_name: string;
  faculty_email: string;
  faculty_department: string;
  file_name?: string;
  file_path?: string;
  file_size?: number;
}

export default function SubmissionReview() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [submission, setSubmission] = useState<SubmissionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [docxPreviewHtml, setDocxPreviewHtml] = useState<string>('');
  const [docxPreviewLoading, setDocxPreviewLoading] = useState(false);
  const [docxPreviewError, setDocxPreviewError] = useState('');

  useEffect(() => {
    const fetchSubmission = async () => {
      if (!id) return;
      try {
        const result = await submissionsAPI.getById(parseInt(id));
        if (!result.error && result.data) {
          setSubmission(result.data);
          setFeedback(result.data.feedback || '');
        }
      } catch (err) {
        console.error('Failed to fetch submission:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSubmission();
  }, [id]);

  const handleApprove = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      const result = await adminAPI.reviewSubmission(parseInt(id), 'approved', feedback);
      if (!result.error) {
        navigate('/admin/submissions');
      }
    } catch (err) {
      console.error('Failed to approve:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!feedback.trim()) {
      alert('Please provide feedback for rejection');
      return;
    }
    if (!id) return;
    setSubmitting(true);
    try {
      const result = await adminAPI.reviewSubmission(parseInt(id), 'rejected', feedback);
      if (!result.error) {
        navigate('/admin/submissions');
      }
    } catch (err) {
      console.error('Failed to reject:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(2)} KB`;
  };

  const fileUrl = submission?.file_path ? resolveServerUrl(submission.file_path) : '';

  const getFileExtension = () => {
    const source = submission?.file_name || submission?.file_path || '';
    const dotIndex = source.lastIndexOf('.');
    if (dotIndex < 0) return '';
    return source.slice(dotIndex + 1).toLowerCase();
  };

  const fileExt = getFileExtension();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileExt);
  const isPdf = fileExt === 'pdf';
  const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExt);
  const isAudio = ['mp3', 'wav', 'ogg', 'm4a'].includes(fileExt);
  const isDocx = fileExt === 'docx';

  useEffect(() => {
    const generateDocxPreview = async () => {
      if (!showPreview || !isDocx || !submission?.file_path) {
        setDocxPreviewHtml('');
        setDocxPreviewError('');
        setDocxPreviewLoading(false);
        return;
      }

      try {
        setDocxPreviewLoading(true);
        setDocxPreviewError('');
        const response = await fetch(fileUrl);
        const arrayBuffer = await response.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setDocxPreviewHtml(result.value);
      } catch (err) {
        console.error('Failed to load DOCX preview:', err);
        setDocxPreviewHtml('');
        setDocxPreviewError('Preview not available for this DOCX file.');
      } finally {
        setDocxPreviewLoading(false);
      }
    };

    generateDocxPreview();
  }, [showPreview, isDocx, submission?.file_path, fileUrl]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!submission) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Submission not found</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/admin/submissions')}
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Submissions
        </button>

        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">{submission.title}</h1>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                  {submission.category_name}
                </span>
                <span className={`inline-flex items-center px-4 py-1.5 rounded-full font-medium ${
                  submission.status === 'pending' 
                    ? 'bg-amber-100 text-amber-700' 
                    : submission.status === 'approved'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {submission.status === 'pending' ? 'Pending Review' : submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                </span>
              </div>
            </div>
          </div>

          {/* Faculty Information */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-semibold text-gray-900 mb-4">Faculty Information</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-gray-600 mb-1">Name</div>
                <div className="font-medium text-gray-900">{submission.faculty_name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Email</div>
                <div className="font-medium text-gray-900">{submission.faculty_email}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Department</div>
                <div className="font-medium text-gray-900">
                  {submission.faculty_department || 'Not specified'}
                </div>
              </div>
            </div>
          </div>

          {/* Submission Details */}
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
            <p className="text-gray-700 leading-relaxed">{submission.description}</p>
          </div>

          {/* Submission Date */}
          <div className="border-t border-gray-200 pt-6 mt-6">
            <div className="flex items-center text-gray-600">
              <Calendar className="w-5 h-5 mr-2" />
              <span>
                Submitted on {new Date(submission.submitted_date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>
        </div>

        {/* File Information */}
        {submission.file_name && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Submitted File</h3>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">{submission.file_name}</div>
                  <div className="text-sm text-gray-600">{formatFileSize(submission.file_size)}</div>
                </div>
              </div>
              {submission.file_path && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPreview(true)}
                    className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View
                  </button>
                  <a
                    href={fileUrl}
                    download={submission.file_name || true}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {showPreview && submission?.file_path && (
          <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPreview(false)}
          >
            <div
              className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">{submission.file_name || 'File Preview'}</h3>
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="flex-1 overflow-auto p-4 flex items-center justify-center">
                {isImage ? (
                  <img src={fileUrl} alt={submission.file_name} className="max-w-full max-h-[70vh] object-contain rounded-lg" />
                ) : isPdf ? (
                  <iframe src={fileUrl} title={submission.file_name} className="w-full h-[70vh] rounded-lg border border-gray-200" />
                ) : isVideo ? (
                  <video controls className="max-w-full max-h-[70vh] rounded-lg">
                    <source src={fileUrl} />
                    Your browser does not support the video tag.
                  </video>
                ) : isAudio ? (
                  <audio controls className="w-full">
                    <source src={fileUrl} />
                  </audio>
                ) : isDocx && !docxPreviewLoading && !docxPreviewError ? (
                  <div className="w-full h-[70vh] overflow-auto rounded-lg border border-gray-200 p-6 bg-white prose max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: docxPreviewHtml }} />
                  </div>
                ) : isDocx && docxPreviewLoading ? (
                  <div className="text-gray-600">Loading DOCX preview...</div>
                ) : (
                  <div className="text-center space-y-4 py-12">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                      <FileText className="w-12 h-12 text-gray-400" />
                    </div>
                    <p className="text-gray-600">
                      {docxPreviewError || 'Preview not available for this file type.'}
                    </p>
                      <a
                        href={fileUrl}
                      download={submission.file_name || true}
                      className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Review Actions */}
        {submission.status === 'pending' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Review & Feedback</h3>
            <div className="mb-6">
              <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                Feedback / Comments
              </label>
              <textarea
                id="feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={5}
                placeholder="Provide feedback for the faculty member..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
              <p className="text-sm text-gray-500 mt-2">
                This feedback will be visible to the faculty member
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={submitting}
                className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors font-semibold disabled:opacity-50"
              >
                <XCircle className="w-5 h-5 mr-2" />
                Reject Submission
              </button>
              <button
                onClick={() => setShowApproveModal(true)}
                disabled={submitting}
                className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Approve & Archive
              </button>
            </div>
          </div>
        )}

        {/* Previous Feedback (for reviewed submissions) */}
        {submission.status !== 'pending' && submission.feedback && (
          <div className={`rounded-xl border p-6 ${
            submission.status === 'approved' 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <h3 className={`font-semibold mb-3 ${
              submission.status === 'approved' ? 'text-green-900' : 'text-red-900'
            }`}>
              Review Feedback
            </h3>
            <p className={submission.status === 'approved' ? 'text-green-700' : 'text-red-700'}>
              {submission.feedback}
            </p>
            {submission.reviewed_date && (
              <p className="text-sm mt-3 opacity-75">
                Reviewed on {new Date(submission.reviewed_date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            )}
          </div>
        )}

        {/* Guidelines */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
          <h3 className="font-semibold text-purple-900 mb-3">Review Guidelines</h3>
          <ul className="space-y-2 text-sm text-purple-700">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Check for originality and creative merit</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Ensure content aligns with university standards</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Provide constructive feedback for improvement</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Review file quality and completeness</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
              Approve Submission?
            </h2>
            <p className="text-gray-600 text-center mb-4">
              This creative work will be approved and added to the archive. The faculty member will
              be notified.
            </p>
            <div className="mb-6">
              <label htmlFor="approve-feedback" className="block text-sm font-medium text-gray-700 mb-2">
                Comments & Feedback
              </label>
              <textarea
                id="approve-feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                placeholder="Add comments or feedback for the faculty member..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
              <p className="text-sm text-gray-500 mt-1">
                This feedback will be visible to the faculty member
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowApproveModal(false)}
                disabled={submitting}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={submitting}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50"
              >
                {submitting ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
              Reject Submission?
            </h2>
            <p className="text-gray-600 text-center mb-4">
              This submission will be rejected. Please provide feedback to help the faculty member improve.
            </p>
            <div className="mb-6">
              <label htmlFor="reject-feedback" className="block text-sm font-medium text-gray-700 mb-2">
                Comments & Feedback <span className="text-red-500">*</span>
              </label>
              <textarea
                id="reject-feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                placeholder="Explain why this submission is being rejected and suggest improvements..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              />
              {!feedback.trim() && (
                <p className="text-sm text-red-500 mt-1">
                  Feedback is required when rejecting a submission
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={submitting}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={submitting || !feedback.trim()}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50"
              >
                {submitting ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
